import { makeAutoObservable, runInAction } from "mobx";
import { QueryClient } from "@tanstack/react-query";

import { ApiError } from "@synthesis/shared-services";
import { setSnack } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import { SourceType, createDirectory, deleteDirectoryRequest, renameDirectoryRequest, type DirectoryCreateDto, type FileMetaDto } from "../../../api";
import { GeneralStore } from "../general-store";
import { StoreState, type StoreStateValue } from "../root-store";
import type { History, SortType } from "../root-store";

const getErrorMessageCode = (err: unknown): string | undefined => {
  if (err instanceof ApiError) {
    return err.response?.errorCode;
  }

  if (typeof err === "object" && err !== null && "response" in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (data && typeof data === "object") {
      if ("messageCode" in data) return String((data as { messageCode?: string }).messageCode ?? "");
      if ("errorCode" in data) return String((data as { errorCode?: string }).errorCode ?? "");
    }
  }

  return undefined;
};

export class DirectoryStore {
  state: StoreStateValue = StoreState.Initial;
  directories: FileMetaDto[] = [];
  rootFolderId = "";
  currentFolder: History | null = null;
  projectId: string | null = null;
  sortType: SortType = "file.ASC";
  generalStore: GeneralStore;

  constructor(projectId: string, generalStore: GeneralStore) {
    this.projectId = projectId;
    this.generalStore = generalStore;
    makeAutoObservable(this);
  }

  async renameDirectory(
    data: {
      name: string;
    },
    folderId: string,
    queryClient: QueryClient,
  ) {
    const body = {
      id: folderId,
      newName: data.name,
    };

    this.state = StoreState.Loading;

    try {
      await renameDirectoryRequest({ body });

      runInAction(() => {
        this.state = StoreState.Success;
        this.generalStore.getContents(this.rootFolderId);

        if (queryClient) queryClient.invalidateQueries({ queryKey: ["filesystem/favorites/root"] });
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
      });

      if (getErrorMessageCode(err) === "file.name.duplicate") {
        setSnack({
          title: "Папка с таким именем уже существует",
          messageType: "error",
        });
      }

      logger.error("renameDirectory", err);
    }
  }

  async addDirectory(data: { name: string }) {
    const body: DirectoryCreateDto = {
      baseName: data.name.trim(),
      sourceId: this.projectId!,
      parentId: this.rootFolderId,
      sourceType: SourceType.PROJECT_ID,
    };

    this.state = StoreState.Loading;

    try {
      await createDirectory({ body });

      runInAction(() => {
        this.state = StoreState.Success;
        this.generalStore.getContents(this.rootFolderId);
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
      });

      if (getErrorMessageCode(err) === "file.name.duplicate") {
        setSnack({
          title: "Папка с таким именем уже существует",
          messageType: "error",
        });
      }

      logger.error("addDerictory", err);
    }
  }

  async deleteDirectory(id: string) {
    this.state = StoreState.Loading;

    try {
      await deleteDirectoryRequest({ path: { id } });

      runInAction(() => {
        this.state = StoreState.Success;
        this.generalStore.getContents(this.rootFolderId);
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
      });
      logger.error("deleteDirectory", err);
    }
  }
}
